import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import * as classnames from 'classnames';
import { RawDraftContentState } from 'draft-js';
import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import { Avatar } from 'wix-ui-tpa/Avatar';

import { Box } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';

import { createGiphyPlugin } from 'wix-rich-content-plugin-giphy';
import { createImagePlugin } from 'wix-rich-content-plugin-image';
import { createVideoPlugin } from 'wix-rich-content-plugin-video';

import {
  WithGroup,
  WithGroupProps,
  withAppSettings,
  WithAppSettingsProps,
  withSiteMembers,
} from '../../Context';
import { VideoCameraIcon } from '../../../icons/VideoCamera';

import styles from './NewPost.st.css';
import {
  withTpaComponentsConfig,
  WithTpaComponentsConfigProps,
} from '../../Context/withTpaComponentsConfig';
import { ImagePluginIcon } from '../../../icons/ImagePluginIcon';
import { WithSiteMembers } from '../../Context/withSiteMembers';
import { Anonymous, SiteMember } from '@wix/social-groups-api';

export interface NewPostBoxProps {
  onClick(): void;
  draft?: RawDraftContentState;
}

// Implementation note 💩: Can not use icons from plugin.InsertPluginButtons, because
// wix-rich-content common icons have conflicts in id="path-1"
const imagePlugin = createImagePlugin({ t: () => {}, pluginDefaults: {} });
imagePlugin.InsertPluginButtons[0].buttonSettings.Icon = ImagePluginIcon;

const icons = [
  { plugin: imagePlugin },
  { plugin: createVideoPlugin({ t: () => {}, pluginDefaults: {} }) },
  {
    plugin: createGiphyPlugin({ t: () => {}, pluginDefaults: {} }),
    className: styles.strokePluginIcon,
  },
].map(({ plugin, className = styles.fillPluginIcon }) => {
  const [button] = plugin.InsertPluginButtons;
  const { Icon, name } = button.buttonSettings;

  if (name === 'Video') {
    return (
      <VideoCameraIcon
        className={classnames(styles.pluginIcon, className)}
        key={name}
      />
    );
  }

  return (
    <Icon
      width={19}
      height={19}
      key={name}
      className={classnames(styles.pluginIcon, className)}
    />
  );
});

const NewPostComponent: React.FC<NewPostBoxProps &
  InjectedTranslateProps &
  WithGroupProps &
  WithAppSettingsProps &
  WithSiteMembers &
  WithTpaComponentsConfigProps> = props => {
  const { onClick, currentSiteMember, t, mobile, appSettings, draft } = props;

  const [imageIcon, , giphyIcon] = icons;
  const iconsToShow = mobile ? [imageIcon, giphyIcon] : icons;

  return (
    <Box
      onClick={onClick}
      data-hook="new-post-box"
      withSideBorders={!mobile}
      {...styles('root', { mobile, hasDraft: !!draft })}
    >
      <Avatar src={currentSiteMember.imageUrl} {...styles('avatar')} />

      {draft ? (
        <Text
          typography={TYPOGRAPHY.runningText}
          {...styles(styles.placeholder, {})}
        >
          {getDraftText()}
        </Text>
      ) : (
        <>
          <Text
            typography={TYPOGRAPHY.runningText}
            {...styles(styles.placeholder, {})}
          >
            {appSettings.text.newPostBoxLabel ||
              t('groups-web.discussion.create-post.placeholder')}
          </Text>
          {iconsToShow}
        </>
      )}
    </Box>
  );

  function getDraftText() {
    if (!draft) {
      return '';
    }

    return draft.blocks
      .filter(block => block.type === 'unstyled')
      .map(block => block.text)
      .join('\n');
  }
};

NewPostComponent.defaultProps = {
  currentSiteMember: Anonymous as SiteMember,
};

const enhance = compose(
  translate(),
  WithGroup,
  withTpaComponentsConfig,
  withAppSettings,
  withSiteMembers,
);

export const NewPost = enhance(NewPostComponent) as React.ComponentType<
  NewPostBoxProps
>;
